import React, { useState, useEffect, FC } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Select,
  DatePicker,
  Input,
  Tag,
  Tooltip,
  Modal,
  Form,
  message,
  Space,
  Badge,
  Drawer,
  Divider,
  Typography,
  Row,
  Col,
  Checkbox,
} from "antd";
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
  ClearOutlined,
  SaveOutlined,
  ExportOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import {
  mockPendingWorkOrdersData,
  mockSiteOptions,
  mockProjectOptions,
  mockContractTypeOptions,
} from "./mockData/pendingWorkOrdersData";
import {
  mockContractorOptions,
  mockContractTypeOptions as mockContractTypeOptionsView,
} from "./mockData/viewWorkOrderData";
import { PendingWorkOrderData, ApprovalStatus, UserRole } from "./types";
import WorkOrderAside from "./workOrderAside";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { useSelector } from "react-redux";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

interface FilterState {
  workOrderNumber?: string;
  workOrderTitle?: string;
  status?: string[];
  siteId?: string[];
  contractorName?: string[];
  contractType?: string;
  createdDateRange?: [any, any] | null;
  releasedDateRange?: [any, any] | null;
  grossValueRange?: { min?: number; max?: number };
  workOrderOwner?: string[];
}

const PendingWorkOrderApprovals: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";

  const [sm, setSm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PendingWorkOrderData[]>([]);
  const [filteredData, setFilteredData] = useState<PendingWorkOrderData[]>([]);
  const [currentUserRole] = useState<UserRole>(UserRole.Checker);
  const [selectedKPIFilter, setSelectedKPIFilter] = useState<string>("status");
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  // Filter states - updated to match ViewWorkOrder structure
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [workOrderStatus, setWorkOrderStatus] = useState<any>([]);
  const [contractorOptions, setContractorOptions] = useState<any>([]);
  const [contractTypeOptions, setContractTypeOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [workOrderOwnerOptions, setWorkOrderOwnerOptions] = useState<any>([]);

  // Drawer states
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [form] = Form.useForm();

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "object" && value !== null) {
          if ("min" in value && value.min !== undefined) count++;
          if ("max" in value && value.max !== undefined) count++;
        } else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  // Modal states
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [isReturnModalVisible, setIsReturnModalVisible] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] =
    useState<PendingWorkOrderData | null>(null);
  const [approvalForm] = Form.useForm();
  const [returnForm] = Form.useForm();

  useEffect(() => {
    loadPendingWorkOrders();
    // Set status options
    const statusOptions = [
      { label: "Draft", value: "Draft" },
      { label: "Submitted", value: "Submitted" },
      { label: "Pending Approval", value: "Pending Approval" },
      { label: "Released", value: "Released" },
      { label: "Approved", value: "Approved" },
      { label: "Rejected", value: "Rejected" },
      { label: "Contractor Confirmed", value: "Contractor Confirmed" },
      { label: "In Execution", value: "In Execution" },
      { label: "On Hold", value: "On Hold" },
      { label: "Variation Pending", value: "Variation Pending" },
      { label: "Amended", value: "Amended" },
      { label: "Completed", value: "Completed" },
      {
        label: "Pending Execution Approval",
        value: "Pending Execution Approval",
      },
      { label: "Cancelled", value: "Cancelled" },
      { label: "Closed", value: "Closed" },
      { label: "Changes Requested", value: "Changes Requested" },
      {
        label: "Pending Contractor Response",
        value: "Pending Contractor Response",
      },
      {
        label: "Changes Under Negotiation",
        value: "Changes Under Negotiation",
      },
      { label: "In Progress", value: "In Progress" },
      { label: "Contractor Rejected", value: "Contractor Rejected" },
    ];
    setWorkOrderStatus(statusOptions);
    setContractTypeOptions(mockContractTypeOptionsView);
  }, []);

  // Fetch contractors/clients based on flowType using Work Order dropdown API

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  const loadPendingWorkOrders = async () => {
    try {
      setLoading(true);
      const roleBasedData = mockPendingWorkOrdersData.filter((wo) => {
        if (currentUserRole === UserRole.Checker) {
          return wo.status === ApprovalStatus.SubmittedForApproval;
        } else if (currentUserRole === UserRole.Verifier) {
          return wo.status === ApprovalStatus.Checked;
        } else if (currentUserRole === UserRole.Approver) {
          return wo.status === ApprovalStatus.Verified;
        }
        return false;
      });

      setData(roleBasedData);
    } catch (error) {
      console.error("Error loading pending work orders:", error);
      message.error("Failed to load pending work orders");
    } finally {
      setLoading(false);
    }
  };

  // Filter Functions - matching ViewWorkOrder
  const handleFilterDrawerOpen = async () => {
    setTempFilters({ ...filters });
    form.setFieldsValue(filters);
    setFilterDrawerVisible(true);

    // Fetch dropdown data when drawer opens
    try {
      const partyType = isClientFlow ? "Client" : "Contractor";

      // Fetch contractors
      const contractorsResponse = await dropdownApis.fetchContractorDropDown(
        partyType
      );
      if (
        contractorsResponse &&
        contractorsResponse.status &&
        contractorsResponse.data
      ) {
        const contractorOptions = contractorsResponse.data.map((item: any) => ({
          label: item.companyName || item.contractorName || "N/A",
          value: item.contractorID?.toString() || item.contractorId?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }

      // Fetch users based on current filter siteIds
      const siteIds =
        filters.siteId && filters.siteId.length > 0
          ? filters.siteId.join(",")
          : undefined;

      const usersResponse = await dropdownApis.fetchUserDropdown(siteIds);
      if (usersResponse && usersResponse.status && usersResponse.data) {
        const userOptions = usersResponse.data.map((item: any) => ({
          label: item.userName || "N/A",
          value: item.appUserId?.toString() || item.userId?.toString(),
        }));
        setWorkOrderOwnerOptions(userOptions);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    form.resetFields();
  };

  const handleApplyFilters = () => {
    form
      .validateFields()
      .then((values) => {
        // Process form values to match our filter state structure
        const processedFilters: FilterState = {
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          contractorName:
            values.contractorName && values.contractorName.length > 0
              ? values.contractorName
              : undefined,
          contractType: values.contractType?.trim() || undefined,
          workOrderOwner:
            values.workOrderOwner && values.workOrderOwner.length > 0
              ? values.workOrderOwner
              : undefined,
        };

        // Remove undefined values
        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        setFilters(processedFilters);
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setFilters({});
    setTempFilters({});
    form.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName.trim()]: { ...filters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setFilters(savedFilter);
      setTempFilters(savedFilter);
      form.setFieldsValue(savedFilter);
    }
  };

  // KPI Filter Categories
  const kpiFilterOptions = [
    { label: "By Status", value: "status" },
    { label: "By Value", value: "value" },
    { label: "By Age", value: "age" },
  ];

  // KPI Filter Change Handler
  const handleKPIFilterChange = (filterValue: string) => {
    setSelectedKPIFilter(filterValue);
  };

  // Dynamic Widget Data Generator
  const getWidgetsByFilter = (filterType: string) => {
    const totalPending = filteredData.length;
    const overdue = filteredData.filter(
      (wo) => calculateAge(wo.lastActionDate) > 3
    ).length;
    const totalValue = filteredData.reduce((sum, wo) => sum + wo.grossValue, 0);
    const avgAge =
      totalPending > 0
        ? Math.round(
            filteredData.reduce(
              (sum, wo) => sum + calculateAge(wo.lastActionDate),
              0
            ) / totalPending
          )
        : 0;

    switch (filterType) {
      case "status":
        const submittedCount = filteredData.filter(
          (wo) => wo.status === ApprovalStatus.SubmittedForApproval
        ).length;
        const checkedCount = filteredData.filter(
          (wo) => wo.status === ApprovalStatus.Checked
        ).length;
        const verifiedCount = filteredData.filter(
          (wo) => wo.status === ApprovalStatus.Verified
        ).length;

        return [
          {
            title: "Total Pending",
            key: "total",
            color: "#495057",
            percentage: "100",
            value: `${totalPending}`,
            icon: "📊",
          },
          // {
          //   title: "Submitted for Approval",
          //   key: "submitted",
          //   color: "#FD7E14",
          //   percentage: totalPending > 0 ? `${Math.round((submittedCount / totalPending) * 100)}` : "0",
          //   value: `${submittedCount}`,
          //   icon: "📝"
          // },
          {
            title: "Checked",
            key: "checked",
            color: "#007BFF",
            percentage:
              totalPending > 0
                ? `${Math.round((checkedCount / totalPending) * 100)}`
                : "0",
            value: `${checkedCount}`,
            icon: "✅",
          },
          {
            title: "Verified",
            key: "verified",
            color: "#17A2B8",
            percentage:
              totalPending > 0
                ? `${Math.round((verifiedCount / totalPending) * 100)}`
                : "0",
            value: `${verifiedCount}`,
            icon: "🔍",
          },
          {
            title: "Overdue (>3 days)",
            key: "overdue",
            color: "#DC3545",
            percentage:
              totalPending > 0
                ? `${Math.round((overdue / totalPending) * 100)}`
                : "0",
            value: `${overdue}`,
            icon: "⚠️",
          },
        ];

      case "value":
        const highValue = filteredData.filter(
          (wo) => wo.grossValue > 1000000
        ).length;
        const mediumValue = filteredData.filter(
          (wo) => wo.grossValue > 500000 && wo.grossValue <= 1000000
        ).length;
        const lowValue = filteredData.filter(
          (wo) => wo.grossValue <= 500000
        ).length;

        return [
          {
            title: "Total Value",
            key: "totalValue",
            color: "#495057",
            percentage: "100",
            value: new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(totalValue),
            icon: "💰",
          },
          {
            title: "High Value (>₹10L)",
            key: "highValue",
            color: "#DC3545",
            percentage:
              totalPending > 0
                ? `${Math.round((highValue / totalPending) * 100)}`
                : "0",
            value: `${highValue}`,
            icon: "💎",
          },
          {
            title: "Medium Value (₹5L-₹10L)",
            key: "mediumValue",
            color: "#FFC107",
            percentage:
              totalPending > 0
                ? `${Math.round((mediumValue / totalPending) * 100)}`
                : "0",
            value: `${mediumValue}`,
            icon: "💼",
          },
          {
            title: "Low Value (<₹5L)",
            key: "lowValue",
            color: "#198754",
            percentage:
              totalPending > 0
                ? `${Math.round((lowValue / totalPending) * 100)}`
                : "0",
            value: `${lowValue}`,
            icon: "💵",
          },
        ];

      case "age":
        const fresh = filteredData.filter(
          (wo) => calculateAge(wo.lastActionDate) <= 1
        ).length;
        const recent = filteredData.filter(
          (wo) =>
            calculateAge(wo.lastActionDate) > 1 &&
            calculateAge(wo.lastActionDate) <= 3
        ).length;
        const old = filteredData.filter(
          (wo) =>
            calculateAge(wo.lastActionDate) > 3 &&
            calculateAge(wo.lastActionDate) <= 7
        ).length;
        const veryOld = filteredData.filter(
          (wo) => calculateAge(wo.lastActionDate) > 7
        ).length;

        return [
          {
            title: "Average Age",
            key: "avgAge",
            color: "#495057",
            percentage: "All WOs",
            value: `${avgAge} days`,
            icon: "📅",
          },
          {
            title: "Fresh (≤1 day)",
            key: "fresh",
            color: "#198754",
            percentage:
              totalPending > 0
                ? `${Math.round((fresh / totalPending) * 100)}`
                : "0",
            value: `${fresh}`,
            icon: "🆕",
          },
          {
            title: "Recent (2-3 days)",
            key: "recent",
            color: "#FFC107",
            percentage:
              totalPending > 0
                ? `${Math.round((recent / totalPending) * 100)}`
                : "0",
            value: `${recent}`,
            icon: "📋",
          },
          {
            title: "Old (4-7 days)",
            key: "old",
            color: "#FD7E14",
            percentage:
              totalPending > 0
                ? `${Math.round((old / totalPending) * 100)}`
                : "0",
            value: `${old}`,
            icon: "⏰",
          },
          {
            title: "Very Old (>7 days)",
            key: "veryOld",
            color: "#DC3545",
            percentage:
              totalPending > 0
                ? `${Math.round((veryOld / totalPending) * 100)}`
                : "0",
            value: `${veryOld}`,
            icon: "🚨",
          },
        ];

      default:
        return [];
    }
  };

  const exportFilteredData = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      "WO Number,Title,Site,Project,Contractor,Value,Status,Last Action By,Last Action Date,Age\n" +
      filteredData
        .map(
          (wo) =>
            `${wo.workOrderNumber},"${wo.workOrderTitle}",${wo.siteName},${
              wo.projectName
            },${wo.contractorName},${wo.grossValue},${wo.status},${
              wo.lastActionBy
            },${wo.lastActionDate},${calculateAge(wo.lastActionDate)}`
        )
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `pending_work_orders_${dayjs().format("YYYY-MM-DD")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success("Data exported successfully");
  };

  const getStatusColor = (status: any) => {
    switch (status) {
      case "Pending Approval":
        return "orange";
      default:
        return "default";
    }
  };

  const getRoleDisplayName = (role: UserRole) => {
    switch (role) {
      case UserRole.Checker:
        return "Checker";
      case UserRole.Verifier:
        return "Verifier";
      case UserRole.Approver:
        return "Approver";
      default:
        return "";
    }
  };

  const getNextActionBy = (status: ApprovalStatus) => {
    switch (status) {
      case ApprovalStatus.SubmittedForApproval:
        return "Checker";
      case ApprovalStatus.Checked:
        return "Verifier";
      case ApprovalStatus.Verified:
        return "Approver";
      default:
        return "";
    }
  };

  const calculateAge = (lastActionDate: string) => {
    return dayjs().diff(dayjs(lastActionDate), "days");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleViewWorkOrder = (record: any) => {
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
    navigate(`/work-order-view/${record.workOrderId}${flowTypeQuery}`);
  };

  const handleApprove = (record: any) => {
    setSelectedWorkOrder(record);
    setIsApprovalModalVisible(true);
  };

  const handleReturn = (record: any) => {
    setSelectedWorkOrder(record);
    setIsReturnModalVisible(true);
  };

  const processApproval = async (values: any) => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const nextStatus =
        currentUserRole === UserRole.Checker
          ? ApprovalStatus.Checked
          : currentUserRole === UserRole.Verifier
          ? ApprovalStatus.Verified
          : ApprovalStatus.Approved;

      message.success(
        `Work Order approved and moved to ${getNextActionBy(nextStatus)}`
      );
      setIsApprovalModalVisible(false);
      approvalForm.resetFields();
      loadPendingWorkOrders();
    } catch (error) {
      message.error("Failed to approve work order");
    }
  };

  const processReturn = async (values: any) => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      message.success("Work Order returned for correction");
      setIsReturnModalVisible(false);
      returnForm.resetFields();
      loadPendingWorkOrders();
    } catch (error) {
      message.error("Failed to return work order");
    }
  };

  const columns = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 250,
      render: (text: string, record: any) => (
        <span
          className="inlineTableCellLink"
          id={`woStatus-${record?.workOrderId}`}
          onClick={() => handleViewWorkOrder(record)}
        >
          {text}
        </span>
        // <Button
        //   type="link"
        //   onClick={() => handleViewWorkOrder(record)}
        //   style={{ padding: 0, height: 'auto' }}
        // >
        //   {text}
        // </Button>
      ),
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Site / Project",
      key: "siteProject",
      width: 180,
      render: (_: any, record: PendingWorkOrderData) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.siteName}</div>
          <div className="text-muted small">{record.projectName || "N/A"}</div>
        </div>
      ),
    },
    {
      title: isClientFlow ? "Client" : "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 150,
      ellipsis: true,
    },
    {
      title: "WO Value",
      dataIndex: "grossValue",
      key: "grossValue",
      width: 120,
      render: (value: number) => (
        <span style={{ fontWeight: 500 }}>{formatCurrency(value)}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (status: ApprovalStatus) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "Last Action By",
      dataIndex: "lastActionBy",
      key: "lastActionBy",
      width: 120,
    },
    {
      title: "Last Action Date",
      dataIndex: "lastActionDate",
      key: "lastActionDate",
      width: 120,
      render: (date: string) => dayjs(date).format("DD-MMM-YYYY"),
    },
    // {
    //   title: "Next Action By",
    //   key: "nextActionBy",
    //   width: 120,
    //   render: (_: any, record: PendingWorkOrderData) => (
    //     <span style={{ fontWeight: 500, color: "#1890ff" }}>
    //       {getNextActionBy(record.status)}
    //     </span>
    //   ),
    // },
    {
      title: "Age (Days)",
      dataIndex: "slaAgingForApproval",
      key: "slaAgingForApproval",
      width: 100,
      render: (text: any, record: PendingWorkOrderData) => {
        const age = text || 0;
        return (
          <span
            style={{
              color: age > 3 ? "#ff4d4f" : age > 1 ? "#faad14" : "#52c41a",
              fontWeight: 500,
            }}
          >
            {age}
          </span>
        );
      },
    },
    // {
    //   title: "Remarks",
    //   dataIndex: "remarks",
    //   key: "remarks",
    //   width: 150,
    //   ellipsis: true,
    // },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   width: 150,
    //   fixed: 'right' as const,
    //   render: (_:any, record: PendingWorkOrderData) => (
    //     <Space>
    //       <Tooltip title="View Details">
    //         <Button
    //           type="primary"
    //           ghost
    //           size="small"
    //           icon={<EyeOutlined />}
    //           onClick={() => handleViewWorkOrder(record)}
    //         />
    //       </Tooltip>
    //       <Tooltip title="Approve">
    //         <Button
    //           type="primary"
    //           size="small"
    //           icon={<CheckOutlined />}
    //           onClick={() => handleApprove(record)}
    //         />
    //       </Tooltip>
    //       <Tooltip title="Return">
    //         <Button
    //           danger
    //           size="small"
    //           icon={<CloseOutlined />}
    //           onClick={() => handleReturn(record)}
    //         />
    //       </Tooltip>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="pending-work-order-approvals"
        title="Pending Work Order Approvals"
        columns={columns}
        customFilters={filters}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <WorkOrderAside updateSm={setSm} sm={sm} />
          </div>
        }
        extra={{
          tableScrollX: 1500,
          dateRange: false,
          excelExport: false,
          dataFilterTootip: "Filtering based on Work Order last action date",
        }}
        // widget={{
        //   isActive: true,
        //   compactGrid: true,
        //   kpiFilter: {
        //     isActive: false,
        //     options: kpiFilterOptions,
        //     selectedFilter: selectedKPIFilter,
        //     onFilterChange: handleKPIFilterChange,
        //   },
        //   list: getWidgetsByFilter(selectedKPIFilter),
        // }}
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleFilterDrawerOpen}
              >
                Advanced Filters
              </Button>
            </Badge>
            {/* <Button 
            type="primary" 
            icon={<ReloadOutlined />}
            onClick={loadPendingWorkOrders}
            loading={loading}
            size="large"
          >
            Refresh
          </Button> */}
          </Space>
        }
      />

      <Drawer
        title="Advanced Filters"
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space size="middle">
            <Badge count={getActiveFilterCount()} size="small">
              <Button size="small" type="text" icon={<FilterOutlined />} />
            </Badge>
            <Button
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Option key={filterName} value={filterName}>
                      {filterName}
                    </Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={filters}
          onValuesChange={(changedValues, allValues) => {
            // When siteId changes, fetch users for those sites
            if (changedValues.siteId !== undefined) {
              const fetchUsers = async () => {
                try {
                  const siteIds =
                    allValues.siteId && allValues.siteId.length > 0
                      ? allValues.siteId.join(",")
                      : undefined;

                  const response = await dropdownApis.fetchUserDropdown(
                    siteIds
                  );
                  if (response && response.status && response.data) {
                    const options = response.data.map((item: any) => ({
                      label: item.userName || "N/A",
                      value:
                        item.appUserId?.toString() || item.userId?.toString(),
                    }));
                    setWorkOrderOwnerOptions(options);
                    // Clear workOrderOwner if it was selected, as users may have changed
                    if (changedValues.siteId) {
                      form.setFieldsValue({ workOrderOwner: undefined });
                    }
                  }
                } catch (error) {
                  console.error("Error fetching work order owners:", error);
                }
              };
              fetchUsers();
            }
          }}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="siteId" label="Site">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="contractorName"
                label={isClientFlow ? "Client" : "Contractor"}
              >
                <Select
                  mode="multiple"
                  placeholder={
                    isClientFlow ? "Select Clients" : "Select Contractors"
                  }
                  options={contractorOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="contractType" label="Contract Type">
                <Select
                  placeholder="Select Contract Type"
                  options={contractTypeOptions}
                  allowClear
                  showSearch
                  filterOption={(input: any, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderOwner" label="Work Order Owner">
                <Select
                  mode="multiple"
                  placeholder="Select Work Order Owners"
                  options={workOrderOwnerOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>

      {/* Approval Modal */}
      <Modal
        title="Approve Work Order"
        open={isApprovalModalVisible}
        onCancel={() => setIsApprovalModalVisible(false)}
        footer={null}
      >
        <Form form={approvalForm} layout="vertical" onFinish={processApproval}>
          <p>
            <strong>Work Order:</strong> {selectedWorkOrder?.workOrderNumber}
            <br />
            <strong>Title:</strong> {selectedWorkOrder?.workOrderTitle}
            <br />
            <strong>Value:</strong>{" "}
            {selectedWorkOrder && formatCurrency(selectedWorkOrder.grossValue)}
          </p>

          <Form.Item name="comments" label="Approval Comments (Optional)">
            <TextArea
              rows={4}
              placeholder="Add any comments or observations..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setIsApprovalModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" icon={<CheckOutlined />}>
                Approve & Forward
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Return Modal */}
      <Modal
        title="Return Work Order for Correction"
        open={isReturnModalVisible}
        onCancel={() => setIsReturnModalVisible(false)}
        footer={null}
      >
        <Form form={returnForm} layout="vertical" onFinish={processReturn}>
          <p>
            <strong>Work Order:</strong> {selectedWorkOrder?.workOrderNumber}
            <br />
            <strong>Title:</strong> {selectedWorkOrder?.workOrderTitle}
          </p>

          <Form.Item
            name="returnComments"
            label="Return Comments"
            rules={[
              {
                required: true,
                message: "Please provide reasons for returning this work order",
              },
              {
                min: 10,
                message: "Comments must be at least 10 characters long",
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Please specify the issues that need to be corrected..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setIsReturnModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                danger
                htmlType="submit"
                icon={<CloseOutlined />}
              >
                Return for Correction
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PendingWorkOrderApprovals;
